"use client";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { type FocusEventHandler, type ReactNode, type Ref } from "react";
import {
  type CountryIso2,
  defaultCountries,
  parseCountry,
} from "react-international-phone";
import AutocompleteTextInput from "@/components/common/formElements/library/AutocompleteTextInput";
import { StyledFlagImage } from "../PhoneInput/styles";

export interface CountryOption {
  iso2: CountryIso2;
  name: string;
  dialCode: string;
}

export interface CountryCodeSelectorProps {
  value?: CountryIso2;
  onChange?: (value?: CountryIso2 | null) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  label?: ReactNode;
  placeholder?: string;
  error?: boolean;
  helperText?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  tooltip?: ReactNode;
  name?: string;
}

export default function CountryCodeSelector(props: CountryCodeSelectorProps) {
  const {
    value = "us",
    onChange,
    onBlur,
    label,
    placeholder,
    error,
    helperText,
    required = false,
    disabled = false,
    inputRef,
    tooltip,
  } = props;

  const countryOptions: CountryOption[] = defaultCountries.map((country) => {
    const parsedCountry = parseCountry(country);
    return {
      iso2: parsedCountry.iso2 as CountryIso2,
      name: parsedCountry.name,
      dialCode: parsedCountry.dialCode,
    };
  });

  const selectedCountry = countryOptions.find(
    (option) => option.dialCode === value,
  );

  return (
    <Autocomplete
      options={countryOptions}
      value={selectedCountry}
      disabled={disabled}
      onChange={(_, newValue) => {
        if (onChange) {
          onChange(newValue ? newValue.dialCode : null);
        }
      }}
      isOptionEqualToValue={(option, value) => option.iso2 === value.iso2}
      getOptionLabel={(option) => `${option.name} (+${option.dialCode})`}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.iso2}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <StyledFlagImage iso2={option.iso2} />
            <Typography>{option.name}</Typography>
            <Typography>+{option.dialCode}</Typography>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <AutocompleteTextInput
          {...params}
          label={label}
          tooltip={tooltip}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          required={required}
          inputRef={inputRef}
          onBlur={onBlur}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: selectedCountry && (
                <Box sx={{ display: "flex", mr: 1 }}>
                  <StyledFlagImage iso2={selectedCountry.iso2} />
                </Box>
              ),
            },
          }}
        />
      )}
    />
  );
}
